import { useMemo } from 'react';
import { max, startOfDay, subDays } from 'date-fns';
import { useApplicationContext } from '../../core/contexts/ApplicationContext.tsx';
import { MIN_DATE } from '../components/DateRangeFilter.tsx';

// current date in the given IANA timezone (as a local Date at midnight)
export const todayInTimezone = (timezone?: string | null): Date => {
    const now = new Date();
    if (timezone) {
        try {
            const parts = new Intl.DateTimeFormat('en', {
                timeZone: timezone,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            }).formatToParts(now);
            const part = (type: string) => Number(parts.find((p) => p.type === type)?.value);
            return new Date(part('year'), part('month') - 1, part('day'));
        } catch {
            // unknown timezone identifier — fall back to the browser's local date
        }
    }
    return startOfDay(now);
};

export const getDataRetentionMinDate = (customer?: {
    dataRetentionDays?: number | null;
    timezone?: string | null;
}): Date | null => {
    const retentionDays = customer?.dataRetentionDays;
    if (!retentionDays || retentionDays <= 0) return null;

    // `today - retentionDays + 1` — a window of exactly `retentionDays` days including today
    return max([MIN_DATE, subDays(todayInTimezone(customer?.timezone), retentionDays - 1)]);
};

export const useDataRetentionMinDate = (): Date | null => {
    const { user } = useApplicationContext();
    const dataRetentionDays = user?.customer?.dataRetentionDays;
    const timezone = user?.customer?.timezone;

    return useMemo(() => getDataRetentionMinDate({ dataRetentionDays, timezone }), [dataRetentionDays, timezone]);
};
